import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plane, MapPin, User, Heart, Star, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarkdownPreview } from '@/components/notes/MarkdownPreview';
import { plazaApi, notesApi } from '@/services/notesApi';
import { useToast } from '@/components/ui/use-toast';
import type { PlazaNoteDetail } from '@/types/notes.types';

export default function PlazaNoteDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [note, setNote] = useState<PlazaNoteDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    plazaApi
      .getPlazaNote(Number(id))
      .then((res) => setNote(res.data.data))
      .catch(() => {
        toast({ title: '获取笔记失败', variant: 'destructive' });
        navigate('/notes/plaza');
      })
      .finally(() => setLoading(false));
  }, [id, navigate, toast]);

  const handleLike = async () => {
    if (!note) return;
    try {
      const res = await notesApi.likeNote(note.id);
      setNote({
        ...note,
        isLiked: res.data.data.isLiked,
        likeCount: res.data.data.likeCount,
      });
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  const handleFavorite = async () => {
    if (!note) return;
    try {
      const res = await notesApi.favoriteNote(note.id);
      setNote({
        ...note,
        isFavorited: res.data.data.isFavorited,
        favoriteCount: res.data.data.favoriteCount,
      });
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!note) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">笔记不存在或已取消分享</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/notes/plaza')}>
          返回广场
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 顶部栏 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/notes/plaza')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">返回广场</span>
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLike}
            className="gap-1.5"
          >
            <Heart
              className={`w-4 h-4 ${note.isLiked ? 'fill-red-500 text-red-500' : ''}`}
            />
            {note.likeCount}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleFavorite}
            className="gap-1.5"
          >
            <Star
              className={`w-4 h-4 ${note.isFavorited ? 'fill-amber-500 text-amber-500' : ''}`}
            />
            {note.favoriteCount}
          </Button>
        </div>
      </div>

      {/* 标题 */}
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{note.title}</h1>

      {/* 元信息 */}
      <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1">
          <User className="w-3.5 h-3.5" />
          {note.authorDisplay}
        </span>
        <span className="inline-flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {new Date(note.createdAt).toLocaleString('zh-CN')}
        </span>
      </div>

      {/* 关联标签 */}
      <div className="flex flex-wrap gap-2 mb-6">
        {note.route && (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg border border-blue-100">
            <Plane className="w-3.5 h-3.5" />
            {note.route.displayName || note.route.codePair}
          </span>
        )}
        {note.airports?.map((ap) => (
          <span
            key={ap.iataCode}
            className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-sm rounded-lg border border-green-100"
          >
            <MapPin className="w-3.5 h-3.5" />
            {ap.iataCode} {ap.nameZh}
          </span>
        ))}
      </div>

      {/* 正文 */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <MarkdownPreview content={note.content} />
      </div>
    </div>
  );
}
